"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FloridaRules = void 0;
const unwrap_1 = require("../../unwrap");
const FEDERAL_1 = require("./FEDERAL");
const FL_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Governing program",
        citation: "Fla. Admin. Code R. 62-730.160(1), 62-730.185(1); 40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Florida adopts 40 CFR part 262 at 62-730.160(1) and 40 CFR part 273 at
      62-730.185(1), so a container is routed to its program exactly as it would be federally.
      Open for confirmation: whether Florida's incorporation by reference reaches 40 CFR part 279
      at all. Used oil has its own Florida chapter, 62-710, outside the parts 260–273 range
      chapter 62-730 takes up, so the USED_OIL branch here is an assumption that Florida's
      used-oil program leaves the generator's storage unclocked — not a reading of 62-710.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "USED_OIL" },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "SUBPART_P_PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: null }, produces: "ORDINARY" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_VSQG_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.14",
    jurisdiction: "FEDERAL",
};
const FEDERAL_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 273.15(a), 273.35(a)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_SUBPART_P_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 266.502(f)(1)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_USED_OIL_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 279.22",
    jurisdiction: "FEDERAL",
};
const FL_FEDERAL_NOTE = (0, unwrap_1.unwrap) `
  Florida adopts this provision by reference without changing it, so the inherited federal answer
  and its CFR citation are shown; that is where the operative text lives.`;
const FL_SQG_200_MILE_NOTE = (0, unwrap_1.unwrap) `
  The only change from federal. Florida's adoption of part 262 excepts 40 CFR 262.16(c), the
  270-day extension for waste that must travel more than 200 miles to a designated facility. With
  the extension never adopted, distance changes nothing: the container stays on the 180 days of
  262.16(b), which Florida did adopt. Stamped Florida because it is Florida's exception, not the
  CFR, that produces the answer.`;
const FL_VSQG_NOTE = (0, unwrap_1.unwrap) `
  Same as the federal VSQG conditions; 62-730.160(1) adopts 262.14 unchanged.`;
const FL_USED_OIL_OPEN_NOTE = (0, unwrap_1.unwrap) `
  OPEN — awaiting specialist confirmation. Chapter 62-730's incorporation by reference is read as
  covering 40 CFR parts 260–273, which leaves out part 279; Florida manages used oil under its own
  chapter 62-710, which has not been read for this table. The federal answer is claimed here on the
  assumption that Florida imposes no generator storage clock on used oil either. If 62-710 does
  impose one, this row and its satellite twin are what change.`;
const FL_SUBPART_P_NOTE = (0, unwrap_1.unwrap) `
  Read as reached by chapter 62-730's incorporation of 40 CFR parts 260–273, which brackets 266
  subpart P; the specific Florida rule adopting part 266 was not read for this table, so the row is
  a federal claim pending the same confirmation as the used-oil rows. Same creditable /
  non-creditable over-strictness caveat as the federal table.`;
const FL_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — federal part 262 as adopted at 62-730.160(1) ─
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "LQG" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
        note: FL_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: false },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
        note: FL_FEDERAL_NOTE,
    },
    // ── The one Florida departure ──────────────────────────────────────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: true },
        expect: {
            clock: {
                kind: "TIME",
                days: 180,
                citation: "Fla. Admin. Code R. 62-730.160(1) (40 CFR 262.16(c) not adopted)",
                jurisdiction: "FL",
            },
        },
        note: FL_SQG_200_MILE_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "VSQG" },
        expect: { clock: FEDERAL_VSQG_NO_LIMIT },
        note: FL_VSQG_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 40 CFR 262.15 adopted unchanged ───────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "LQG" },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: FL_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "SQG" },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: FL_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "VSQG" },
        expect: { caps: FEDERAL_VSQG_NO_LIMIT, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: FL_VSQG_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: null },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── UNIVERSAL WASTE — 40 CFR part 273 adopted at 62-730.185(1) ─────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_UNIVERSAL_WASTE_CLOCK },
        note: FL_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_UNIVERSAL_WASTE_CLOCK,
        },
        note: FL_FEDERAL_NOTE,
    },
    // ── USED OIL — federal part 279, subject to the open question above ────
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_USED_OIL_NO_LIMIT },
        note: FL_USED_OIL_OPEN_NOTE,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_USED_OIL_NO_LIMIT,
        },
        note: FL_USED_OIL_OPEN_NOTE,
    },
    // ── SUBPART P PHARMACEUTICALS — federal part 266 ───────────────────────
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_SUBPART_P_CLOCK },
        note: FL_SUBPART_P_NOTE,
    },
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_SUBPART_P_CLOCK,
        },
        note: FL_SUBPART_P_NOTE,
    },
];
/**
 * FLORIDA ACCUMULATION THRESHOLD EXPECTATIONS — Fla. Admin. Code R.
 * 62-730.160(1), adopting 40 CFR 262.14 and 262.16
 *
 * Florida's numbers are the federal ones: its exception reaches 262.16(c)
 * alone, and 262.16(c) is a time extension rather than a quantity. The
 * inherited federal slot already answers correctly and this class overrides no
 * threshold behavior. The table is authored anyway rather than inherited: a
 * state's claims are what the specialist reviews and what the SME packet
 * prints under its heading, so inheriting would put federal rows behind
 * Florida's name without anyone having read them for Florida.
 */
const FL_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
    },
];
function effectiveFlWasteKind(ctx) {
    if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
        return "UNIVERSAL_WASTE";
    }
    if (ctx.wasteKind === "USED_OIL") {
        return "USED_OIL";
    }
    if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
        return "SUBPART_P_PHARMACEUTICAL";
    }
    return "ORDINARY";
}
/** Florida (DEP) accumulation limits, Fla. Admin. Code R. 62-730.160. */
class FloridaRules extends FEDERAL_1.FederalOnlyRules {
    constructor() {
        super(...arguments);
        this.expectations = FL_EXPECTATIONS;
        this.accumulationThresholdExpectations = FL_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = FL_DERIVATIONS;
    }
    centralClock(ctx) {
        // The kind programs run on their own parts, untouched by an exception to
        // part 262.
        if (effectiveFlWasteKind(ctx) !== "ORDINARY") {
            return super.centralClock(ctx);
        }
        // 262.16(c) is the one provision Florida left out, so the 200-mile
        // extension never arrives and the SQG stays on adopted 262.16(b)'s 180
        // days. Every other status is federal law as adopted.
        if (ctx.generatorStatus === "SQG" && ctx.mustTransport200Miles) {
            return {
                kind: "TIME",
                days: 180,
                citation: "Fla. Admin. Code R. 62-730.160(1) (40 CFR 262.16(c) not adopted)",
                jurisdiction: "FL",
            };
        }
        return super.centralClock(ctx);
    }
}
exports.FloridaRules = FloridaRules;
//# sourceMappingURL=FL.js.map